import { api, LightningElement } from "lwc";

import { getClient } from "@devforce/trailhead-client-api";
import { logger } from "@devforce/trailhead-client-logger";

import { trackUserActivity, TrackEvent } from "common/track";

import {
  DownloadLink,
  DownloadLinks,
  FooterContent,
  FooterLink,
} from "./types";

// Type guard to tell DownloadLinks vs DownloadLink[] apart.
function isDownloadLinksObject(
  links?: DownloadLink[] | DownloadLinks
): links is DownloadLinks {
  return !Array.isArray(links);
}

export default class extends LightningElement {
  @api modulePath: string = "/api/v1/ui/chrome.js";
  @api apiPath?: string | null;
  @api locale?: string | null;

  private content: FooterContent = null!;

  private get downloadLinks() {
    // make backward compatible
    // could be an array of DownloadLink
    // or an object with heading:String and items:DownloadLink[]

    if (!this.content.downloadLinks) return undefined;

    const links: DownloadLink[] = isDownloadLinksObject(
      this.content.downloadLinks
    )
      ? this.content.downloadLinks.items
      : this.content.downloadLinks;

    return links.map((download) => {
      return Object.assign({}, download, {
        handleClick: this.handleLinkClick.bind(this, download.link),
      });
    });
  }

  private get socialLinks() {
    if (!this.content.social) return undefined;

    return this.content.social.links.map((link) => {
      return Object.assign({}, link, {
        iconClassName: `icon--${link.type}`,
        handleClick: this.handleLinkClick.bind(this, link),
      });
    });
  }

  private get downloadLinksHeading() {
    return isDownloadLinksObject(this.content.downloadLinks)
      ? this.content.downloadLinks.heading
      : undefined;
  }

  private bubbleLinkClick(e: CustomEvent) {
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
  }

  private bubbleLocaleChange(e: CustomEvent) {
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
  }

  private handleLinkClick(link: FooterLink) {
    this.dispatchEvent(new CustomEvent("link_click", { detail: link }));
    trackUserActivity(this, "link_clicked", {
      label: link.label,
      pageLocation: "th-footer",
    });
  }

  private handleTrack(e: TrackEvent<"tds-footer">) {
    let pageLocation = "th-footer";
    switch (e.detail.eventName) {
      default:
        return trackUserActivity(this, e.detail.eventName, {
          label: e.detail.payload.label,
          pageLocation,
        });
    }
  }

  private configPath(path: string) {
    if (this.locale) {
      path = `${path}?locale=${this.locale}`;
    }
    return path;
  }

  connectedCallback() {
    if (this.apiPath) {
      getClient()
        .get<FooterContent>(this.configPath(this.apiPath))
        .then((footer) => {
          this.content = footer;
        })
        .catch((e: Error) => logger.warn(e.message));
    } else {
      getClient()
        .import<{ footer: FooterContent }>(this.configPath(this.modulePath))
        .then(({ footer }) => {
          this.content = footer;
        })
        .catch((e: Error) => logger.warn(e.message));
    }
  }
}
